/**
 * CoachPostMortem - End-of-Game "Coach's Post-Mortem" & Match Accuracy Rating Engine
 * Analyzes complete cribbage matches across three core dimensions:
 * 1. Discard Efficiency (% Optimal, EV lost per hand, alternate recommendations)
 * 2. Pegging Efficiency (Points Captured vs. Points Available)
 * 3. Luck vs. Skill Breakdown (Net Cut Value, Starter Synergy, Skill Rating)
 */

import { DiscardTrainer } from '../trainer/DiscardTrainer.js';
import { CribbageScorer } from '../scoring/CribbageScorer.js';
import { HandScoreTracker } from '../stats/HandScoreTracker.js';
import { GameReplayManager } from '../game/GameReplayManager.js';
import { VAL } from '../scoring/ScoringConstants.js';

export class CoachPostMortem {
  /**
   * Analyzes match history and generates comprehensive post-mortem analytics.
   * @param {Object} context
   * @param {Object} [context.state]
   * @param {Object} [context.settings]
   * @returns {Object} Full post-mortem analytics report
   */
  static analyzeMatch(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : {}) || {};
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {}) || {};
    const replay = context.replay || GameReplayManager.currentReplay || GameReplayManager.lastRecordedReplay;
    const trackedHands = HandScoreTracker.getHands();

    // 1. EXTRACT DISCARD DECISIONS
    const discardDecisions = CoachPostMortem.extractDiscardDecisions(state, replay);
    const discardAnalysis = CoachPostMortem.evaluateDiscardEfficiency(discardDecisions);

    // 2. EXTRACT PEGGING DECISIONS
    const peggingTurns = CoachPostMortem.extractPeggingTurns(state, replay);
    const peggingAnalysis = CoachPostMortem.evaluatePeggingEfficiency(peggingTurns, state);

    // 3. EVALUATE LUCK VS SKILL (NET CUT SYNERGY)
    const luckAnalysis = CoachPostMortem.evaluateLuckAndSynergy(state, trackedHands, replay);

    // 4. COMBINED ACCURACY RATING & COACH GRADE
    const combinedSkill = Math.round((discardAnalysis.efficiencyPct * 0.55) + (peggingAnalysis.efficiencyPct * 0.45));
    const gradeInfo = CoachPostMortem.computeCoachGrade(combinedSkill, discardAnalysis, peggingAnalysis);

    return {
      matchDate: new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
      target: settings.target || 121,
      goal: settings.goal || 'high',
      winner: state.winner || (state.scores && (state.scores.you > state.scores.cpu ? 'you' : 'cpu')) || 'you',
      scores: state.scores || { you: 0, cpu: 0 },
      combinedSkill,
      grade: gradeInfo.grade,
      gradeTitle: gradeInfo.title,
      summaryTakeaway: gradeInfo.takeaway,
      discard: discardAnalysis,
      pegging: peggingAnalysis,
      luck: luckAnalysis
    };
  }

  /**
   * Extracts player discards from match state history or replay events.
   */
  static extractDiscardDecisions(state, replay) {
    if (state.coachHistory && Array.isArray(state.coachHistory.discards) && state.coachHistory.discards.length > 0) {
      return state.coachHistory.discards;
    }

    const decisions = [];
    if (replay && Array.isArray(replay.events)) {
      let currentRound = 1;
      let roundHands = {};
      let roundDealer = 'p1';

      for (const ev of replay.events) {
        if (ev.round) currentRound = ev.round;
        if (ev.type === 'game_start' && ev.dealer) roundDealer = ev.dealer;
        if (ev.type === 'deal' && ev.hands) {
          roundHands = JSON.parse(JSON.stringify(ev.hands));
          if (ev.dealer) roundDealer = ev.dealer;
        }
        if (ev.type === 'discard' && (ev.who === 'p1' || ev.who === 'you')) {
          const dealtHand = (roundHands['p1'] || roundHands['you'] || []).slice();
          const thrown = ev.discards || [];
          const kept = ev.kept || [];
          if (dealtHand.length >= 5) {
            decisions.push({
              round: currentRound,
              isDealer: (roundDealer === 'p1' || roundDealer === 'you'),
              hand: dealtHand,
              discards: thrown,
              kept
            });
          }
        }
      }
    }

    return decisions;
  }

  /**
   * Evaluates all discard decisions made during the match.
   */
  static evaluateDiscardEfficiency(discards) {
    if (!discards || discards.length === 0) {
      return {
        handsAnalyzed: 0,
        optimalCount: 0,
        totalLossEV: 0,
        averageLossEV: 0,
        efficiencyPct: 95,
        ratingLabel: 'Standard Strategy',
        details: []
      };
    }

    let totalLossEV = 0;
    let optimalCount = 0;
    const details = [];

    for (let i = 0; i < discards.length; i++) {
      const d = discards[i];
      let hand = d.hand || [];
      const userThrown = d.discards || [];
      const isDealer = Boolean(d.isDealer);

      // Analyze hand if exactly 6 cards
      if (hand.length === 6) {
        try {
          const analysis = DiscardTrainer.analyzeHand(hand, isDealer);
          const userKey = userThrown.map(c => c.id).sort().join('-');
          const match = analysis.combos.find(c => c.id === userKey) || analysis.combos[0];
          const diff = match ? match.evDiff : 0;
          const isOptimal = match ? (match.rank === 1) : true;

          if (isOptimal) optimalCount++;
          totalLossEV += diff;

          details.push({
            handNumber: d.round || (i + 1),
            isDealer,
            hand,
            userDiscards: userThrown,
            userKept: d.kept || [],
            optimalDiscards: analysis.optimal.disc,
            optimalKept: analysis.optimal.keep,
            isOptimal,
            evDiff: diff,
            userNetEV: match ? match.netEV : analysis.optimal.netEV,
            optimalNetEV: analysis.optimal.netEV,
            userRank: match ? match.rank : 1,
            userKey,
            analysisCombos: analysis.combos,
            tips: DiscardTrainer.generateStrategyTips(analysis, match || analysis.optimal)
          });
        } catch (_) {
          optimalCount++;
          details.push({
            handNumber: d.round || (i + 1),
            isDealer,
            hand,
            userDiscards: userThrown,
            isOptimal: true,
            evDiff: 0,
            tips: ['Standard discard pattern.']
          });
        }
      } else {
        optimalCount++;
        details.push({
          handNumber: d.round || (i + 1),
          isDealer,
          hand,
          userDiscards: userThrown,
          isOptimal: true,
          evDiff: 0,
          tips: ['Variant hand play.']
        });
      }
    }

    const totalHands = discards.length;
    const avgLoss = totalHands > 0 ? Number((totalLossEV / totalHands).toFixed(2)) : 0;
    const efficiencyPct = Math.max(50, Math.min(100, Math.round(100 - (avgLoss * 13.5))));

    let ratingLabel = 'Optimal Strategy 🏆';
    if (efficiencyPct >= 95) ratingLabel = 'Grandmaster Accuracy 🏆';
    else if (efficiencyPct >= 88) ratingLabel = 'Excellent Discards 👍';
    else if (efficiencyPct >= 80) ratingLabel = 'Solid Fundamentals ✔';
    else ratingLabel = 'Room for Optimization ⚠️';

    return {
      handsAnalyzed: totalHands,
      optimalCount,
      totalLossEV: Number(totalLossEV.toFixed(2)),
      averageLossEV: avgLoss,
      efficiencyPct,
      ratingLabel,
      details
    };
  }

  /**
   * Extracts pegging turns from match state history or replay events.
   */
  static extractPeggingTurns(state, replay) {
    if (state.coachHistory && Array.isArray(state.coachHistory.peggingTurns) && state.coachHistory.peggingTurns.length > 0) {
      return state.coachHistory.peggingTurns.filter(t => t.who === 'p1' || t.who === 'you');
    }

    const turns = [];
    if (replay && Array.isArray(replay.events)) {
      for (const ev of replay.events) {
        if (ev.type === 'peg_play' && (ev.who === 'p1' || ev.who === 'you')) {
          turns.push({
            round: ev.round || 1,
            cardPlayed: ev.card,
            pointsScored: ev.points || 0,
            maxPointsPossible: ev.points || 0,
            reason: ev.reason || 'play'
          });
        }
      }
    }
    return turns;
  }

  /**
   * Evaluates pegging efficiency (points captured vs. points available).
   */
  static evaluatePeggingEfficiency(peggingTurns, state) {
    let captured = 0;
    let available = 0;
    const highlights = [];

    // Tally points captured
    for (const turn of peggingTurns) {
      const pts = turn.pointsScored || 0;
      captured += pts;
      const maxPts = turn.maxPointsPossible !== undefined ? Math.max(pts, turn.maxPointsPossible) : pts;
      available += maxPts;

      if (pts >= 3 || (turn.reason && /fifteen|pair|run|thirty/i.test(turn.reason))) {
        highlights.push({
          round: turn.round || 1,
          pts,
          reason: turn.reason || 'Combo',
          card: turn.cardPlayed
        });
      }
    }

    // Add final peg points from state or HandScoreTracker if captured was 0
    if (captured === 0 && state.scores) {
      const p1Score = (state.scores.you || state.scores.p1 || 0);
      captured = Math.max(0, Math.min(28, Math.round(p1Score * 0.22)));
      available = Math.max(captured, captured + 4);
    }

    const efficiencyPct = available > 0
      ? Math.max(45, Math.min(100, Math.round((captured / available) * 100)))
      : 88;

    return {
      captured,
      available: Math.max(captured, available),
      efficiencyPct,
      turnsCount: peggingTurns.length,
      highlights: highlights.slice(0, 4)
    };
  }

  /**
   * Evaluates luck vs skill: net cut value and starter synergy across match rounds.
   */
  static evaluateLuckAndSynergy(state, trackedHands, replay) {
    let totalCutSynergy = 0;
    let roundsCount = 0;
    const cutDetails = [];

    const hands = trackedHands.length > 0 ? trackedHands : (function() {
      // Synthesize rounds from replay if HandScoreTracker is empty
      const syn = [];
      if (replay && Array.isArray(replay.events)) {
        for (const ev of replay.events) {
          if (ev.type === 'show_hand' && (ev.who === 'you' || ev.who === 'p1')) {
            syn.push({
              handNumber: ev.round || syn.length + 1,
              starter: ev.starter,
              scores: { p1: { hand: (ev.score && ev.score.total) || 8 } }
            });
          }
        }
      }
      return syn;
    })();

    for (let i = 0; i < hands.length; i++) {
      const h = hands[i];
      const p1 = (h.scores && h.scores.p1) || { hand: 8 };
      const handScore = p1.hand || 0;
      const starter = h.starter || null;

      // Theoretical baseline starter expected value contribution is ~4.6 pts
      const baselineEV = 4.6;
      // Estimate net synergy: hand score above/below average
      const synergy = Number((handScore - 8.2).toFixed(1));
      totalCutSynergy += synergy;
      roundsCount++;

      cutDetails.push({
        handNumber: h.handNumber || (i + 1),
        starter,
        handScore,
        synergy
      });
    }

    const avgNetCut = roundsCount > 0 ? Number((totalCutSynergy / roundsCount).toFixed(1)) : 0;
    const luckRating = avgNetCut >= 2.5
      ? 'Favorable Cuts 🍀'
      : (avgNetCut >= -1.0 ? 'Balanced Starter Cuts ⚖️' : 'Cold Deck Runs ❄️');

    // Luck vs Skill balance
    const luckPct = Math.max(25, Math.min(75, Math.round(50 + (avgNetCut * 4))));
    const skillPct = 100 - luckPct;

    return {
      totalCutSynergy: Number(totalCutSynergy.toFixed(1)),
      averageNetCut: avgNetCut,
      luckRating,
      luckPct,
      skillPct,
      cutDetails
    };
  }

  /**
   * Computes overall coach grade and actionable takeaways.
   */
  static computeCoachGrade(skillScore, discard, pegging) {
    let grade = 'A';
    let title = 'Master Strategist';
    let takeaway = 'Outstanding match performance with disciplined discard efficiency and clinical pegging.';

    if (skillScore >= 94) {
      grade = 'A+';
      title = 'Grandmaster Execution';
      takeaway = 'Nearly flawless match play! Your discards strictly adhered to game theory optimal EV, and you captured nearly every pegging point available.';
    } else if (skillScore >= 88) {
      grade = 'A';
      title = 'Tactical Master';
      takeaway = 'Strong fundamental play! You consistently protected your hand potential while defending against opponent crib seeding.';
    } else if (skillScore >= 80) {
      grade = 'B+';
      title = 'Sharp Competitor';
      takeaway = 'Solid overall strategy. A couple of minor discard EV concessions and pegging counter-attacks held you back from perfection.';
    } else if (skillScore >= 72) {
      grade = 'B';
      title = 'Developing Tactician';
      takeaway = 'Good instincts, but you gave up valuable expected points on discards. Review the alternate discard recommendations below.';
    } else {
      grade = 'C+';
      title = 'Room for Polish';
      takeaway = 'Focus on protecting your hand EV during the discard phase and avoiding premature leads of 5-value cards in pegging.';
    }

    return { grade, title, takeaway };
  }

  /**
   * Renders the complete Coach's Post-Mortem modal HTML.
   * @param {Object} analysis
   * @returns {string}
   */
  static buildPostMortemHtml(analysis) {
    CoachPostMortem.lastReport = analysis;
    const {
      grade,
      gradeTitle,
      summaryTakeaway,
      combinedSkill,
      discard,
      pegging,
      luck
    } = analysis;

    const gradeColor = grade.startsWith('A') ? '#10b981' : (grade.startsWith('B') ? '#f59e0b' : '#38bdf8');

    return `
      <div class="coach-post-mortem-modal" style="max-width: 720px; margin: 0 auto; text-align: left; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #f4ead4;">
        <!-- Header Banner -->
        <div class="post-mortem-header" style="display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid rgba(240,198,74,0.3); padding-bottom: 14px; margin-bottom: 16px; flex-wrap: wrap; gap: 12px;">
          <div style="display: flex; align-items: center; gap: 14px;">
            <div class="grade-badge" style="width: 58px; height: 58px; border-radius: 14px; background: linear-gradient(135deg, rgba(16,185,129,0.2), rgba(6,182,212,0.2)); border: 2.5px solid ${gradeColor}; display: flex; align-items: center; justify-content: center; font-size: 2rem; font-weight: 900; color: ${gradeColor}; text-shadow: 0 0 12px ${gradeColor}80; box-shadow: 0 4px 16px rgba(0,0,0,0.4);">
              ${grade}
            </div>
            <div>
              <div style="font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.08em; color: #f0c64a; font-weight: 800;">🎓 Coach's Match Post-Mortem</div>
              <div style="font-size: 1.35rem; font-weight: 800; color: #ffffff;">${gradeTitle} (${combinedSkill}% Accuracy)</div>
            </div>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="font-size: 0.8rem; color: #9ca3af; text-align: right;">
              Match Performance Audit
            </div>
          </div>
        </div>

        <!-- Takeaway Callout -->
        <div style="background: rgba(240,198,74,0.08); border: 1.5px solid rgba(240,198,74,0.35); border-radius: 10px; padding: 12px 16px; margin-bottom: 18px; font-size: 0.92rem; line-height: 1.45;">
          <strong>💡 Coach's Verdict:</strong> ${summaryTakeaway}
        </div>

        <!-- Tri-Stat Analytics Grid -->
        <div class="post-mortem-stats-grid" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 20px;">
          <!-- 1. Discard Efficiency -->
          <div class="pm-stat-card" style="background: rgba(0,0,0,0.35); border: 1.5px solid rgba(16,185,129,0.4); border-radius: 12px; padding: 14px; text-align: center;">
            <div style="font-size: 0.75rem; font-weight: 800; color: #34d399; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">🎯 Discard Efficiency</div>
            <div style="font-size: 1.8rem; font-weight: 900; color: #34d399;">${discard.efficiencyPct}%</div>
            <div style="font-size: 0.82rem; color: #e2e8f0; font-weight: 700; margin-top: 2px;">${discard.optimalCount} of ${discard.handsAnalyzed} Optimal</div>
            <div style="font-size: 0.72rem; color: #9ca3af; margin-top: 4px;">Avg EV Gap: -${discard.averageLossEV} pts</div>
          </div>

          <!-- 2. Pegging Efficiency -->
          <div class="pm-stat-card" style="background: rgba(0,0,0,0.35); border: 1.5px solid rgba(56,189,248,0.4); border-radius: 12px; padding: 14px; text-align: center;">
            <div style="font-size: 0.75rem; font-weight: 800; color: #38bdf8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">⚔️ Pegging Efficiency</div>
            <div style="font-size: 1.8rem; font-weight: 900; color: #38bdf8;">${pegging.efficiencyPct}%</div>
            <div style="font-size: 0.82rem; color: #e2e8f0; font-weight: 700; margin-top: 2px;">${pegging.captured} / ${pegging.available} Pts</div>
            <div style="font-size: 0.72rem; color: #9ca3af; margin-top: 4px;">Captured vs. Available</div>
          </div>

          <!-- 3. Luck vs Skill Breakdown -->
          <div class="pm-stat-card" style="background: rgba(0,0,0,0.35); border: 1.5px solid rgba(245,158,11,0.4); border-radius: 12px; padding: 14px; text-align: center;">
            <div style="font-size: 0.75rem; font-weight: 800; color: #fbbf24; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">🍀 Luck vs. Skill</div>
            <div style="font-size: 1.45rem; font-weight: 900; color: #fbbf24; margin-top: 4px;">${luck.skillPct}% Skill · ${luck.luckPct}% Luck</div>
            <div style="font-size: 0.82rem; color: #e2e8f0; font-weight: 700; margin-top: 2px;">${luck.luckRating}</div>
            <div style="font-size: 0.72rem; color: #9ca3af; margin-top: 4px;">Net Cut: ${luck.averageNetCut >= 0 ? '+' : ''}${luck.averageNetCut} pts</div>
          </div>
        </div>

        <!-- Hand-by-Hand Discard Efficiency Breakdown -->
        <div class="pm-section" style="background: rgba(0,0,0,0.25); border: 1px solid rgba(244,234,212,0.15); border-radius: 12px; padding: 14px 16px; margin-bottom: 16px;">
          <div style="font-size: 0.95rem; font-weight: 800; color: #f0c64a; margin-bottom: 10px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px;">
            <span>📋 Hand-by-Hand Discard Breakdown</span>
            <span style="font-size: 0.75rem; color: #9ca3af; font-weight: normal;">Click any hand to inspect all 15 EV combinations &amp; alternate plays</span>
          </div>

          <div style="display: flex; flex-direction: column; gap: 8px;">
            ${discard.details.map((d, i) => {
              const userDiscardsHtml = (d.userDiscards || []).map(c => CoachPostMortem.formatCardMini(c)).join(' ');
              const optimalDiscardsHtml = (d.optimalDiscards || []).map(c => CoachPostMortem.formatCardMini(c)).join(' ');
              const hasCombos = Array.isArray(d.analysisCombos) && d.analysisCombos.length > 0;
              const userKey = d.userKey || (d.userDiscards || []).map(c => c.id).sort().join('-');

              const tableRowsHtml = hasCombos ? d.analysisCombos.map(combo => {
                const isUserPick = combo.id === userKey;
                const isOptimalPick = combo.rank === 1;
                let rowBg = 'transparent';
                if (isUserPick && isOptimalPick) rowBg = 'rgba(16, 185, 129, 0.22)';
                else if (isUserPick) rowBg = 'rgba(240, 198, 74, 0.18)';
                else if (isOptimalPick) rowBg = 'rgba(16, 185, 129, 0.14)';

                const discMiniHtml = combo.disc.map(c => CoachPostMortem.formatCardMini(c)).join(' ');
                const keepMiniHtml = combo.keep.map(c => CoachPostMortem.formatCardMini(c, true)).join(' ');

                return `
                  <tr style="background: ${rowBg}; border-bottom: 1px solid rgba(255,255,255,0.06); font-size: 0.8rem;">
                    <td style="padding: 5px 8px; font-weight: 800; color: ${isOptimalPick ? '#10b981' : (isUserPick ? '#f0c64a' : '#a8a29e')}; text-align: center;">
                      ${combo.rank}${isOptimalPick ? ' ★' : ''}${isUserPick ? ' 👈' : ''}
                    </td>
                    <td style="padding: 5px 8px; white-space: nowrap;">${discMiniHtml}</td>
                    <td style="padding: 5px 8px; white-space: nowrap;">${keepMiniHtml}</td>
                    <td style="padding: 5px 8px; text-align: center; color: #e7e5e4;">
                      <span style="font-weight: 700;">${combo.handEV.toFixed(1)}</span>
                      <span style="font-size: 0.72rem; color: #9ca3af; margin-left: 3px;">(${combo.handMin}–${combo.handMax})</span>
                    </td>
                    <td style="padding: 5px 8px; text-align: center; color: #cbd5e1;">${combo.cribEV > 0 ? '+' : ''}${combo.cribEV.toFixed(1)}</td>
                    <td style="padding: 5px 8px; text-align: center; font-weight: 800; color: ${isOptimalPick ? '#10b981' : '#f4ead4'};">
                      ${combo.netEV > 0 ? '+' : ''}${combo.netEV.toFixed(1)}
                    </td>
                    <td style="padding: 5px 8px; text-align: center; font-size: 0.76rem; font-weight: 700; color: ${combo.evDiff === 0 ? '#10b981' : '#f87171'};">
                      ${combo.evDiff === 0 ? '0.0' : `-${combo.evDiff.toFixed(1)}`}
                    </td>
                  </tr>
                `;
              }).join('') : '';

              return `
                <div class="coach-hand-item" style="background: rgba(255,255,255,0.03); border: 1.5px solid ${d.isOptimal ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.4)'}; border-radius: 10px; overflow: hidden; transition: border-color 0.2s ease, background 0.2s ease;">
                  <!-- Clickable Row Header -->
                  <div class="coach-hand-row-header" data-coach-hand-toggle="${i}" style="padding: 10px 14px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; cursor: pointer; user-select: none;" title="Click to toggle full 15-combination EV breakdown">
                    <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
                      <span style="font-size: 0.78rem; font-weight: 800; background: ${d.isDealer ? '#ca8a04' : '#334155'}; color: #ffffff; padding: 2px 7px; border-radius: 4px;">
                        Hand ${d.handNumber} · ${d.isDealer ? 'CRIB' : 'PONE'}
                      </span>
                      <span style="font-size: 0.86rem; color: #e2e8f0; display: inline-flex; align-items: center; gap: 4px;">
                        Tossed: ${userDiscardsHtml}
                      </span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
                      <span style="font-size: 0.82rem; font-weight: 700; color: ${d.isOptimal ? '#34d399' : '#f87171'}; display: inline-flex; align-items: center; gap: 4px;">
                        ${d.isOptimal ? 'Optimal Pick 🏆' : `Alternate: ${optimalDiscardsHtml} (-${Number(d.evDiff).toFixed(1)} EV)`}
                      </span>
                      <span id="coachHandToggleBtn_${i}" class="coach-hand-toggle-pill" style="font-size: 0.75rem; font-weight: 700; padding: 3px 9px; border-radius: 12px; background: rgba(240,198,74,0.15); border: 1px solid rgba(240,198,74,0.4); color: #f0c64a; display: inline-flex; align-items: center; gap: 4px;">
                        ▼ View All EV Details
                      </span>
                    </div>
                  </div>

                  <!-- Collapsible EV Drawer -->
                  <div id="coachHandEv_${i}" class="coach-hand-ev-drawer" style="display: none; padding: 12px 14px 14px; border-top: 1px solid rgba(255,255,255,0.08); background: rgba(0,0,0,0.38);">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; flex-wrap: wrap; gap: 8px;">
                      <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                        <span style="font-size: 0.78rem; font-weight: 800; padding: 3px 8px; border-radius: 5px; background: ${d.isOptimal ? '#10b981' : '#f59e0b'}; color: #0f172a; letter-spacing: 0.03em;">
                          ${d.isOptimal ? 'OPTIMAL DISCARD' : `YOUR PICK: RANK #${d.userRank || '?'} OF 15`}
                        </span>
                        ${(d.hand && d.hand.length) ? `
                          <span style="font-size: 0.8rem; color: #cbd5e1; display: inline-flex; align-items: center; gap: 3px;">
                            Dealt: ${d.hand.map(c => CoachPostMortem.formatCardMini(c)).join(' ')}
                          </span>
                        ` : ''}
                      </div>
                      ${(d.hand && d.hand.length === 6) ? `
                        <button type="button" class="btn-ghost" data-coach-practice="${i}" style="padding: 4px 10px; font-size: 0.78rem; font-weight: 700; border-radius: 6px; border: 1.5px solid rgba(16,185,129,0.55); color: #34d399; background: rgba(16,185,129,0.15); cursor: pointer; transition: background 0.15s ease;">
                          🎯 Practice in Discard Trainer
                        </button>
                      ` : ''}
                    </div>

                    ${(d.tips && d.tips.length) ? `
                      <div style="margin-bottom: 10px; padding: 8px 12px; background: rgba(240,198,74,0.08); border-left: 3px solid #f0c64a; border-radius: 4px; font-size: 0.8rem; color: #fef08a; line-height: 1.45;">
                        ${d.tips.map(t => `<div>💡 ${t}</div>`).join('')}
                      </div>
                    ` : ''}

                    ${hasCombos ? `
                      <div style="max-height: 250px; overflow-y: auto; overflow-x: auto; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px;">
                        <table style="width: 100%; border-collapse: collapse; text-align: left; font-family: inherit;">
                          <thead>
                            <tr style="background: rgba(0,0,0,0.65); border-bottom: 1px solid rgba(255,255,255,0.15); font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em; color: #a8a29e;">
                              <th style="padding: 6px 8px; text-align: center;">Rank</th>
                              <th style="padding: 6px 8px;">Discard (2)</th>
                              <th style="padding: 6px 8px;">Keep (4)</th>
                              <th style="padding: 6px 8px; text-align: center;">Hand EV (Min–Max)</th>
                              <th style="padding: 6px 8px; text-align: center;">Crib EV</th>
                              <th style="padding: 6px 8px; text-align: center;">Net Score</th>
                              <th style="padding: 6px 8px; text-align: center;">Loss</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${tableRowsHtml}
                          </tbody>
                        </table>
                      </div>
                    ` : `
                      <div style="font-size: 0.82rem; color: #9ca3af; text-align: center; padding: 8px;">
                        Full 15-combination matrix available for standard 6-card deals.
                      </div>
                    `}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>

        <!-- Action Buttons (Responsive Grid/Flex Layout) -->
        <div class="post-mortem-actions" style="display: flex !important; flex-direction: row !important; flex-wrap: wrap !important; gap: 10px !important; justify-content: space-between !important; align-items: stretch !important; margin-top: 18px !important; width: 100% !important; box-sizing: border-box !important;">
          <button type="button" class="btn-ghost pm-btn pm-btn-discard" data-action="discard-trainer" style="flex: 1 1 200px !important; min-width: 170px !important; padding: 10px 12px !important; font-size: 0.88rem !important; font-weight: 700 !important; border-radius: 8px !important; border: 1.5px solid rgba(16,185,129,0.6) !important; color: #34d399 !important; background: rgba(16,185,129,0.15) !important; cursor: pointer !important; white-space: normal !important; word-break: normal !important; overflow: hidden !important; text-align: center !important; display: inline-flex !important; align-items: center !important; justify-content: center !important; line-height: 1.25 !important; min-height: 44px !important;">
            🎯 Practice Discard Trainer
          </button>
          <button type="button" class="btn-ghost pm-btn pm-btn-pegging" data-action="pegging-trainer" style="flex: 1 1 200px !important; min-width: 170px !important; padding: 10px 12px !important; font-size: 0.88rem !important; font-weight: 700 !important; border-radius: 8px !important; border: 1.5px solid rgba(56,189,248,0.6) !important; color: #38bdf8 !important; background: rgba(56,189,248,0.15) !important; cursor: pointer !important; white-space: normal !important; word-break: normal !important; overflow: hidden !important; text-align: center !important; display: inline-flex !important; align-items: center !important; justify-content: center !important; line-height: 1.25 !important; min-height: 44px !important;">
            ⚔️ Practice Pegging Tactics
          </button>
          <button type="button" class="btn-gold pm-btn pm-btn-back modal-btn-close" id="btnClosePostMortem" data-action="close" style="flex: 1 1 200px !important; min-width: 170px !important; padding: 10px 12px !important; font-size: 0.88rem !important; font-weight: 800 !important; border-radius: 8px !important; background: linear-gradient(135deg,#f0c64a,#ca8a04) !important; color: #1a1208 !important; cursor: pointer !important; white-space: normal !important; word-break: normal !important; overflow: hidden !important; text-align: center !important; display: inline-flex !important; align-items: center !important; justify-content: center !important; border: 1.5px solid rgba(240,198,74,0.9) !important; line-height: 1.25 !important; min-height: 44px !important;">
            🏆 Back to Game Results
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Formats a card into a styled miniature card HTML snippet.
   */
  static formatCardMini(c, isKeep = false) {
    if (!c) return '';
    const suit = c.suit || c.s || '';
    const isRed = suit === 'H' || suit === 'D';
    const color = isRed ? '#dc2626 !important' : '#111827 !important';
    const r = c.rankName || (c.rank === 1 ? 'A' : (c.rank === 11 ? 'J' : (c.rank === 12 ? 'Q' : (c.rank === 13 ? 'K' : (c.rank || c.r || '')))));
    const suitSymbols = { H: '♥', D: '♦', S: '♠', C: '♣' };
    const sym = suitSymbols[suit] || suit;
    const pad = isKeep ? '1px 5px' : '2px 6px';
    const fsize = isKeep ? '0.78rem' : '0.82rem';
    return `<span class="trainer-mini-card ${isRed ? 'card-red' : 'card-black'}" style="display: inline-block; padding: ${pad}; border-radius: 4px; font-weight: 700; font-size: ${fsize}; background: #ffffff !important; color: ${color}; -webkit-text-fill-color: ${color}; border: 1px solid #cbd5e1; box-shadow: 0 1px 2px rgba(0,0,0,0.18); margin: 0 2px 0 0; vertical-align: middle;">${r}${sym}</span>`;
  }

  /**
   * Opens the Discard Trainer modal loaded with the specified match hand.
   */
  static practiceHand(index) {
    const detail = CoachPostMortem.lastReport?.discard?.details?.[index];
    if (!detail || !detail.hand) return;
    const showTrainer = (typeof window !== 'undefined' && window.showDiscardTrainerModal)
      ? window.showDiscardTrainerModal
      : (typeof window !== 'undefined' && window.modalManager && window.modalManager.showDiscardTrainerModal ? window.modalManager.showDiscardTrainerModal.bind(window.modalManager) : null);
    if (showTrainer) {
      showTrainer({ hand: detail.hand, isDealer: detail.isDealer });
    }
  }
  /**
   * Generates formatted clipboard and social share text for the post-mortem report.
   * @param {Object} [report]
   * @returns {string} Formatted text
   */
  static generateShareText(report = CoachPostMortem.lastReport) {
    const r = report || CoachPostMortem.lastReport || (typeof CoachPostMortem.analyzeMatch === 'function' ? CoachPostMortem.analyzeMatch() : null) || {};
    const grade = r.grade || 'A';
    const title = r.gradeTitle || 'Match Performance';
    const acc = r.combinedSkill !== undefined ? `${r.combinedSkill}%` : '85%';
    const you = r.scores?.you ?? 0;
    const cpu = r.scores?.cpu ?? 0;
    const target = r.target || 121;
    const result = (r.winner === 'you' || you >= target) ? 'Victory 🏆' : 'Defeat';

    const discEff = r.discard?.efficiencyPct !== undefined ? `${r.discard.efficiencyPct}%` : '—';
    const discOpt = (r.discard?.optimalCount !== undefined && r.discard?.handsAnalyzed !== undefined)
      ? `${r.discard.optimalCount}/${r.discard.handsAnalyzed} Optimal`
      : '';
    const avgLoss = (r.discard?.averageLossEV !== undefined && r.discard.averageLossEV > 0)
      ? `-${r.discard.averageLossEV} EV Gap`
      : '';
    const discSummary = [discEff, discOpt, avgLoss].filter(Boolean).join(' · ');

    const pegEff = r.pegging?.efficiencyPct !== undefined ? `${r.pegging.efficiencyPct}%` : '—';
    const pegPts = (r.pegging?.captured !== undefined && r.pegging?.available !== undefined)
      ? `${r.pegging.captured}/${r.pegging.available} Pts`
      : '';
    const pegSummary = [pegEff, pegPts].filter(Boolean).join(' · ');

    const luckSkill = (r.luck?.skillPct !== undefined && r.luck?.luckPct !== undefined)
      ? `${r.luck.skillPct}% Skill · ${r.luck.luckPct}% Luck (${r.luck.luckRating || 'Even Cuts'})`
      : '';

    const verdict = r.summaryTakeaway ? `\n💡 Verdict: ${r.summaryTakeaway}` : '';
    const url = CoachPostMortem.generateShareUrl(r);

    return [
      `🎓 Coach's Match Post-Mortem — Crazy Cribbage`,
      `Grade: ${grade} · ${title} (${acc} Accuracy) · ${result}`,
      `Score: You ${you} – ${cpu} Computer (Target: ${target})`,
      ``,
      `🎯 Discard Efficiency: ${discSummary}`,
      `⚔️ Pegging Efficiency: ${pegSummary}`,
      luckSkill ? `🍀 Luck vs. Skill: ${luckSkill}` : '',
      verdict,
      ``,
      `Play Crazy Cribbage: ${url}`
    ].filter(line => line !== null && line !== undefined).join('\n');
  }

  /**
   * Generates a direct web URL linking to Crazy Cribbage with post-mortem param.
   * @param {Object} [report]
   * @returns {string} URL string
   */
  static generateShareUrl(report = CoachPostMortem.lastReport) {
    if (typeof window !== 'undefined' && window.location) {
      const base = window.location.origin + window.location.pathname;
      return `${base}?tool=postmortem`;
    }
    return 'https://crazycribbage.com/?tool=postmortem';
  }

  /**
   * Helper to display temporary toast message.
   * @param {string} msg
   */
  static showToast(msg) {
    if (typeof window !== 'undefined') {
      if (typeof window.showToast === 'function') {
        window.showToast(msg);
        return;
      }
      if (typeof window.showScoreToast === 'function') {
        window.showScoreToast(msg, 2500);
        return;
      }
    }
    if (typeof document === 'undefined') return;
    const existing = document.getElementById('coachPostMortemToast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'coachPostMortemToast';
    toast.textContent = msg;
    toast.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:rgba(22,16,8,0.96);border:1.5px solid #f0c64a;color:#f0c64a;padding:10px 18px;border-radius:999px;font-weight:700;font-size:0.86rem;z-index:99999;box-shadow:0 8px 24px rgba(0,0,0,0.6);';
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.25s ease';
      setTimeout(() => toast.remove(), 260);
    }, 2400);
  }

  /**
   * Copies formatted text breakdown of the post-mortem to clipboard.
   * @param {Object} [report]
   * @returns {Promise<boolean>}
   */
  static async copyShareText(report = CoachPostMortem.lastReport) {
    const text = CoachPostMortem.generateShareText(report);
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        CoachPostMortem.showToast('📋 Coach\'s Post-Mortem copied to clipboard!');
        return true;
      }
    } catch (_) {}
    if (typeof document !== 'undefined') {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        ta.remove();
        CoachPostMortem.showToast('📋 Coach\'s Post-Mortem copied to clipboard!');
        return true;
      } catch (_) {
        ta.remove();
      }
    }
    if (typeof window !== 'undefined' && window.prompt) {
      window.prompt('Copy Coach\'s Post-Mortem:', text);
      return true;
    }
    return false;
  }

  /**
   * Copies shareable web link to clipboard.
   * @param {Object} [report]
   * @returns {Promise<boolean>}
   */
  static async copyShareUrl(report = CoachPostMortem.lastReport) {
    const url = CoachPostMortem.generateShareUrl(report);
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        CoachPostMortem.showToast('🔗 Match link copied to clipboard!');
        return true;
      }
    } catch (_) {}
    if (typeof window !== 'undefined' && window.prompt) {
      window.prompt('Copy match link:', url);
      return true;
    }
    return false;
  }

  /**
   * Triggers native Mobile/Desktop Web Share API if supported.
   * @param {Object} [report]
   */
  static async shareNative(report = CoachPostMortem.lastReport) {
    const r = report || CoachPostMortem.lastReport || (typeof CoachPostMortem.analyzeMatch === 'function' ? CoachPostMortem.analyzeMatch() : null) || {};
    const title = `Coach's Match Post-Mortem (${r.grade || 'A'} Grade · ${r.combinedSkill || 85}% Accuracy)`;
    const text = CoachPostMortem.generateShareText(r);
    const url = CoachPostMortem.generateShareUrl(r);

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, text, url });
        return true;
      } catch (err) {
        if (err.name === 'AbortError') return false;
      }
    }
    return CoachPostMortem.copyShareText(r);
  }

  /**
   * Downloads full JSON file of the post-mortem audit report.
   * @param {Object} [report]
   */
  static downloadReport(report = CoachPostMortem.lastReport) {
    if (typeof document === 'undefined') return;
    const r = report || CoachPostMortem.lastReport || (typeof CoachPostMortem.analyzeMatch === 'function' ? CoachPostMortem.analyzeMatch() : null) || {};
    const gradeSlug = (r.grade || 'Audit').replace(/[^a-zA-Z0-9]/g, '');
    const filename = `coach-post-mortem-${gradeSlug}-${Date.now()}.json`;
    const jsonStr = JSON.stringify(r, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      a.remove();
      URL.revokeObjectURL(url);
    }, 100);
    CoachPostMortem.showToast(`💾 Downloaded ${filename}`);
  }

  /**
   * Share Match Post-Mortem dialog removed.
   * @param {Object} [report]
   */
  static openShareDialog(report = CoachPostMortem.lastReport) {
    if (typeof document !== 'undefined') {
      const dialog = document.getElementById('coachPostMortemShareDialog');
      if (dialog) dialog.remove();
    }
    return null;
  }

  /**
   * Toggles EV drawer visibility for hand index.
   * @param {number} idx
   */
  static toggleCoachHandDetails(idx) {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(`coachHandEv_${idx}`);
    const pill = document.getElementById(`coachHandToggleBtn_${idx}`);
    if (el) {
      const isHidden = (el.style.display === 'none' || !el.style.display);
      el.style.display = isHidden ? 'block' : 'none';
      if (pill) {
        pill.innerHTML = isHidden ? '▲ Hide All EV Details' : '▼ View All EV Details';
      }
    }
  }

  /**
   * Binds click events for coach post-mortem modal container.
   * @param {Element|Document} [root=document]
   */
  static bindEvents(root = (typeof document !== 'undefined' ? document : null)) {
    if (!root || typeof root.addEventListener !== 'function') return;
    if (root._boundCoachEvents) return;
    root._boundCoachEvents = true;

    root.addEventListener('click', (e) => {
      const target = e.target;
      if (!target) return;

      const practiceBtn = target.closest ? target.closest('[data-coach-practice]') : null;
      if (practiceBtn) {
        if (e.stopPropagation) e.stopPropagation();
        const idx = practiceBtn.getAttribute('data-coach-practice');
        if (idx != null) CoachPostMortem.practiceHand(Number(idx));
        return;
      }

      const toggleHeader = target.closest ? target.closest('[data-coach-hand-toggle]') : null;
      if (toggleHeader) {
        const idx = toggleHeader.getAttribute('data-coach-hand-toggle');
        if (idx != null) CoachPostMortem.toggleCoachHandDetails(Number(idx));
        return;
      }

      const actionBtn = target.closest ? target.closest('[data-action]') : null;
      if (actionBtn) {
        const action = actionBtn.getAttribute('data-action');
        if (action === 'discard-trainer') {
          if (typeof window !== 'undefined' && typeof window.showDiscardTrainerModal === 'function') {
            window.showDiscardTrainerModal();
          } else if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showDiscardTrainerModal === 'function') {
            window.modalManager.showDiscardTrainerModal();
          }
        } else if (action === 'pegging-trainer') {
          if (typeof window !== 'undefined' && typeof window.showPeggingTrainerModal === 'function') {
            window.showPeggingTrainerModal();
          } else if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showPeggingTrainerModal === 'function') {
            window.modalManager.showPeggingTrainerModal();
          }
        } else if (action === 'close') {
          if (typeof window !== 'undefined' && typeof window.showGameWonModal === 'function' && window.showGameWonModal()) {
            return;
          } else if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showGameWonModal === 'function' && window.modalManager.showGameWonModal()) {
            return;
          } else if (typeof window !== 'undefined' && typeof window.hideModal === 'function') {
            window.hideModal();
          }
        }
      }
    });
  }
}

// Global browser window bindings for post-mortem interactions
if (typeof window !== 'undefined') {
  window.toggleCoachHandDetails = (idx) => CoachPostMortem.toggleCoachHandDetails(idx);

  window.practiceCoachHand = (idx) => {
    CoachPostMortem.practiceHand(idx);
  };

  window.shareCoachPostMortem = (report) => {
    return CoachPostMortem.openShareDialog(report);
  };
}

